import React, { useEffect, useState } from 'react';
import {
    Box, Button, FormControl, FormLabel, Input, Modal, ModalBody, ModalCloseButton,
    ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Textarea, useToast, VStack,
} from '@chakra-ui/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_AWS_API_URL || '';
const FIRST_ORDER_SUBJECT = 'Everything starts from 1. Place one order this week.';
const FIRST_ORDER_BODY = `Everything starts from 1.

One order. That is how every wash and fold business that lasts actually started. Not a hundred customers. Not a perfect setup. One bag, paid, finished, and paid out to you.

This week, be your own first customer.

Open your booking page. Place one real order with your own card. A small bag is enough. Then open the operator screen and run that same order all the way through: intake, weigh, complete, and charge.

You will see three things a setup checklist cannot teach:

What your customer feels when they book and pay.

What you do as the operator to finish the work.

How the charge shows as Paid, and how Stripe deposits that money to the bank account you connected.

Until you have walked that loop, you are hoping. After you have walked it, you know. Then you can look a real neighbor in the eye and say, I have done this.

Do not wait for a stranger to go first. You are allowed to be order number one. The few dollars on your own card are not wasted. They are how you learn the product you are about to sell.

If anything feels confusing, reply to this email. We will walk it with you.

Go place the order. Finish it. Watch the money. That is the start.`;

const RecipientList = React.memo(function RecipientList({ platformKey, onCount }) {
    const [recipients, setRecipients] = useState(null);
    const [failed, setFailed] = useState(false);
    const toast = useToast();

    useEffect(() => {
        let cancel = false;
        axios.get(`${API_URL}/api/platform/tenant-notice`, { headers: { 'x-platform-key': platformKey } })
            .then((res) => {
                if (cancel) return;
                setRecipients(res.data);
                onCount(res.data?.bccCount || 0, res.data?.to);
            })
            .catch((err) => {
                if (cancel) return;
                setFailed(true);
                toast({ title: 'Could not load shops', description: err.response?.data?.detail || err.message, status: 'error' });
            });
        return () => { cancel = true; };
    }, [platformKey, onCount]);

    if (failed) return <Text fontSize="sm" color="red.500">The shop list did not load. Close this and try again.</Text>;
    if (!recipients) return <Text fontSize="sm" color="gray.500">Checking which shops have an email…</Text>;
    return (
        <Box>
            <Text fontSize="sm" fontWeight="700" mb={1}>{recipients.bccCount} shops on BCC</Text>
            <VStack align="stretch" spacing={1} maxH="140px" overflowY="auto">
                {(recipients.shops || []).map((shop) => (
                    <Text key={shop.laundryId} fontSize="xs" color="gray.600">
                        {shop.laundryName} · {shop.email}
                    </Text>
                ))}
            </VStack>
        </Box>
    );
});

export default function TenantMailModal({ isOpen, onClose, platformKey }) {
    const [subject, setSubject] = useState(FIRST_ORDER_SUBJECT);
    const [text, setText] = useState(FIRST_ORDER_BODY);
    const [bccCount, setBccCount] = useState(0);
    const [toAddress, setToAddress] = useState('info@smartlaundrybasket.ai');
    const [sending, setSending] = useState(false);
    const toast = useToast();

    const onCount = React.useCallback((count, to) => {
        setBccCount(count || 0);
        if (to) setToAddress(to);
    }, []);

    const close = () => {
        if (!sending) onClose();
    };

    const send = async () => {
        if (!subject.trim() || !text.trim()) {
            toast({ title: 'Add a subject and a message', status: 'warning' });
            return;
        }
        if (!window.confirm(`Send this to ${toAddress}? ${bccCount} shops are BCC and will not see each other.`)) return;
        setSending(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/platform/tenant-notice`,
                { subject: subject.trim(), text },
                { headers: { 'x-platform-key': platformKey } },
            );
            const ok = res.data.status === 'success';
            toast({
                title: ok ? 'Email sent' : 'Not fully sent',
                description: res.data.message,
                status: ok ? 'success' : 'error',
                duration: 8000,
                isClosable: true,
            });
            if (ok) onClose();
        } catch (err) {
            toast({ title: 'Error', description: err.response?.data?.detail || err.message, status: 'error' });
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={close} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Email all tenants</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack align="stretch" spacing={3}>
                        <Text fontSize="sm" color="gray.600">
                            The message goes to {toAddress}. Shops with an email are BCC, so they do not see each other.
                        </Text>
                        <FormControl>
                            <FormLabel>Subject</FormLabel>
                            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
                        </FormControl>
                        <FormControl>
                            <FormLabel>Message</FormLabel>
                            <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} />
                        </FormControl>
                        {isOpen ? <RecipientList platformKey={platformKey} onCount={onCount} /> : null}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={close} isDisabled={sending}>Cancel</Button>
                    <Button colorScheme="orange" onClick={send} isLoading={sending} isDisabled={!bccCount}>
                        Send
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
